import { Injectable, EventEmitter } from '@angular/core';
import { AppConfigService } from './app-config.service';
import { BehaviorSubject } from 'rxjs';
import { ClientSettings, SessionSettings } from '../models/client-settings.model';
import { StringHelper } from '../utils/string-helper';

@Injectable({
  providedIn: 'root'
})
export class StorageService {

  static UI_CULTURE = 'uiCulture';
  static LOGIN_TOKEN = 'CLX.LoginToken';
  static REFRESH_TOKEN = 'CLX.RefreshToken';
  static TOKEN_EXPIRE = 'CLX.TokenExpire';
  static PERSON_ID = 'personId';
  static USER_ID = 'userId';
  static ENTRY_POINT = 'entryPoint';
  static PKCE_CODE_VERIFIER = 'pkceCodeVerifier';
  static CLIENT_SETTINGS = 'clientSettings';
  static SESSION_SETTINGS = 'sessionSettings';

  private readonly onClientSettingsChange = new EventEmitter<ClientSettings>(true);

  private readonly onSessionSettingsChange = new EventEmitter<SessionSettings>(true);

  readonly clientSettings: BehaviorSubject<ClientSettings>;

  readonly sessionSettings: BehaviorSubject<SessionSettings>;

  constructor() {
    this.onClientSettingsChange.emit(this.getClientSettings());
    this.onSessionSettingsChange.emit(this.getSessionSettings());

    // init settings emitter
    this.clientSettings = new BehaviorSubject<ClientSettings>(this.getClientSettings());
    this.onClientSettingsChange.subscribe((settings) => {
      this.clientSettings.next(settings);
    });

    this.sessionSettings = new BehaviorSubject<SessionSettings>(this.getSessionSettings());
    this.onSessionSettingsChange.subscribe((settings) => {
      this.sessionSettings.next(settings);
    });
  }

  getUiCulture(): string {
    return this.getLocal(StorageService.UI_CULTURE);
  }

  setUiCulture(value: string): void {
    this.setLocal(StorageService.UI_CULTURE, value);
  }

  getLoginToken(): string {
    return this.getLocal(StorageService.LOGIN_TOKEN);
  }

  setLoginToken(token: string): void {
    return this.setLocal(StorageService.LOGIN_TOKEN, token);
  }

  getRefreshToken(): string {
    return this.getLocal(StorageService.REFRESH_TOKEN);
  }

  setRefreshToken(token: string): void {
    return this.setLocal(StorageService.REFRESH_TOKEN, token);
  }

  getTokenExpire(): number {
    return this.getLocal(StorageService.TOKEN_EXPIRE);
  }

  setTokenExpire(seconds: number): void {
    return this.setLocal(StorageService.TOKEN_EXPIRE, seconds);
  }

  getPersonId(): number {
    const token = StringHelper.parseToken(this.getLoginToken());
    return token.id_person ? Number(token.id_person) : null;
  }

  getUserFullname(): string {
    const tokenString = this.getLoginToken();
    if (!tokenString) {
      return null;
    }

    const token = StringHelper.parseToken(this.getLoginToken());
    return token.fullname ? token.fullname : null;
  }

  getUserId(): number {
    return this.getLocal(StorageService.USER_ID);
  }

  setEntryPointUrl(url: string): void {
    this.setSession(StorageService.ENTRY_POINT, url);
  }

  getEntryPointUrl(): string {
    return this.getSession(StorageService.ENTRY_POINT);
  }

  updateClientSettings(factory: (currentSettings: ClientSettings) => void): void {
    const settings = this.getClientSettings();
    factory(settings);
    this.setLocal(StorageService.CLIENT_SETTINGS, settings);
    this.onClientSettingsChange.emit(settings);
  }

  private getClientSettings(): ClientSettings {
    const settings = this.getLocal<ClientSettings>(StorageService.CLIENT_SETTINGS);
    return settings === null ? new ClientSettings() : settings;
  }

  updateSessionSettings(factory: (currentSettings: SessionSettings) => void): void {
    const settings = this.getSessionSettings();
    factory(settings);
    this.setSession(StorageService.SESSION_SETTINGS, settings);
    this.onSessionSettingsChange.emit(settings);
  }

  /**
   * Returns the PKCE code_verifier from session storage (if any)
   *
   * @return {string}  {string}
   * @memberof StorageService
   */
  getPkceCodeVerifier(): string {
    return this.getSession(StorageService.PKCE_CODE_VERIFIER);
  }

  /**
   * Saves the PKCE code_verifier to session storage (delete if null)
   *
   * @param {string} value
   * @memberof StorageService
   */
  setPkceCodeVerifier(value: string) {
    this.setSession(StorageService.PKCE_CODE_VERIFIER, value);
  }

  /**
   * Clear session- and local storage
   *
   * @memberof StorageService
   */
  clearAll(): void {
    window.localStorage.clear();
    window.sessionStorage.clear();
  }

  private getSessionSettings(): SessionSettings {
    const settings = this.getSession<SessionSettings>(StorageService.SESSION_SETTINGS);
    return settings === null ? new SessionSettings() : settings;
  }

  /**
   * Get localStorage value
   *
   * @private
   * @param {string} key
   * @returns {*}
   * @memberof StorageService
   */
  private getLocal<T>(key: string): T {
    let value = window.localStorage.getItem(this.getKey(key));
    if (value === null) value = window.localStorage.getItem(key);

    return <T>JSON.parse(value);
  }

  /**
   * Set localStorage value
   *
   * @private
   * @param {string} key
   * @param {*} value
   * @memberof StorageService
   */
  private setLocal(key: string, value: any): void {
    if (value === null) {
      window.localStorage.removeItem(this.getKey(key));
    } else {
      window.localStorage.setItem(this.getKey(key), JSON.stringify(value));
    }
  }

  /**
   * Get sessionStorage value
   *
   * @private
   * @param {string} key
   * @returns {*}
   * @memberof StorageService
   */
  private getSession<T>(key: string): any {
    return <T>JSON.parse(window.sessionStorage.getItem(this.getKey(key)));
  }

  /**
   * Set sessionStorage value
   *
   * @private
   * @param {string} key
   * @param {*} value
   * @memberof StorageService
   */
  private setSession(key: string, value: any): void {
    if (value === null) {
      window.sessionStorage.removeItem(this.getKey(key));
    } else {
      window.sessionStorage.setItem(this.getKey(key), JSON.stringify(value));
    }
  }

  /**
   * Appends the instanceId to the given key
   *
   * @private
   * @param {string} key
   * @returns
   * @memberof StorageService
   */
  private getKey(key: string) {
    return AppConfigService.settings.instanceId + '_' + AppConfigService.settings.appScope + '_' + key;
  }

}
